import React, { useEffect, useState, useCallback } from "react";
import { useSignPersonalMessage } from "@mysten/dapp-kit";
import { Gift, Copy, Check, Coins, Users, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, shortAddr } from "@/lib/api";
import { fmtSui, timeAgo } from "@/lib/format";
import { getWalletProof } from "@/lib/walletProof";
import { SITE_URL } from "@/lib/socials";

export default function ReferralCard({ address }) {
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const { mutateAsync: signPersonalMessage } = useSignPersonalMessage();

  const load = useCallback(async () => {
    if (!address) return;
    try { const { data } = await api.get(`/referral/${address}`); setRef(data); } catch (e) {}
  }, [address]);

  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  if (!ref) return null;

  const link = `${SITE_URL}/?ref=${encodeURIComponent(ref.code)}`;
  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true); toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const claimable = ref.claimable_sui || 0;
  const canClaim = claimable >= (ref.min_claim_sui || 0.02);

  const claim = async () => {
    setClaiming(true);
    try {
      const proof = await getWalletProof(signPersonalMessage, address, "claim_referral");
      const { data } = await api.post(`/referral/claim`, { address, ...proof });
      const pid = data.payout_id;
      toast.message(`Claiming ${fmtSui(data.amount_sui, 4)} SUI — confirming on-chain…`);
      // Poll the real payout outcome so we never show a false success (and surface failures).
      let settled = false;
      const started = Date.now();
      while (pid && Date.now() - started < 45000) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: st } = await api.get(`/referral/payout/${pid}`);
          if (st.status === "paid") {
            toast.success(`Paid ${fmtSui(st.amount_sui, 4)} SUI to your wallet${st.digest ? ` · ${st.digest.slice(0, 8)}…` : ""}`);
            settled = true; break;
          }
          if (st.status === "failed") {
            toast.error(`Claim failed: ${st.error || "payout error"}`);
            settled = true; break;
          }
        } catch (e) {}
      }
      if (!settled) toast.message("Claim submitted — still processing. Check your wallet shortly.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    } finally { setClaiming(false); }
  };

  return (
    <div className="card-surface p-5 border border-[#1EC62E]/30" style={{ background: "linear-gradient(90deg,rgba(124,242,78,0.06),transparent)" }} data-testid="referral-card">
      <div className="flex items-center gap-2 mb-1">
        <Gift className="w-4 h-4 text-[#1EC62E]" />
        <div className="font-display font-bold">Refer & earn</div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Share your link. Earn <b className="text-[#1EC62E]">10% of the trade fee</b> from everyone who trades through it — paid straight to your wallet.
      </p>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-lg px-3 py-2 font-mono text-xs truncate" data-testid="referral-link">{link}</div>
        <button onClick={copy} data-testid="referral-copy-button" className="shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs text-black" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg bg-white/[0.03] border border-white/8 px-3 py-2.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Users className="w-3 h-3" /> Referred</div>
          <div className="font-mono font-bold text-lg mt-0.5" data-testid="referral-count">{ref.referred_count || 0}</div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/8 px-3 py-2.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Coins className="w-3 h-3" /> Earned</div>
          <div className="font-mono font-bold text-lg mt-0.5 text-[#1EC62E]" data-testid="referral-earned">{fmtSui(ref.earned_sui || 0, 4)}</div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/8 px-3 py-2.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Wallet className="w-3 h-3" /> Claimable</div>
          <div className="font-mono font-bold text-lg mt-0.5 text-[#1EC62E]" data-testid="referral-claimable">{fmtSui(claimable, 4)}</div>
        </div>
      </div>

      <button onClick={claim} disabled={!canClaim || claiming} data-testid="referral-claim-button" className="w-full py-2.5 rounded-lg font-bold text-black disabled:opacity-40" style={{ background: "linear-gradient(90deg,#7CF24E,#1EC62E)" }}>
        {claiming ? "Claiming…" : canClaim ? `Claim ${fmtSui(claimable, 4)} SUI` : `Min ${fmtSui(ref.min_claim_sui || 0.02, 2)} SUI to claim`}
      </button>

      {(ref.recent || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Recent rewards</div>
          <div className="space-y-1.5" data-testid="referral-recent">
            {ref.recent.slice(0, 8).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs font-mono">
                <span className="text-muted-foreground">{shortAddr(r.trader)} · {r.ts ? timeAgo(r.ts) : ""}</span>
                <span className="text-[#1EC62E]">+{fmtSui(r.reward_sui, 5)} SUI</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
